/**
 * The across-scan pick, at the press seam.
 *
 * Extracted from the Breakthroughs panel for the reason `promotePress` was:
 * what the control SHOWS and what a press DOES have to be one reading, and the
 * only way to pin that is for both to come through functions a Node-only test
 * can call. The regressions this file exists to fail are small enough to
 * survive review — `scanOpts` dropping `among` would send every across press
 * back to recency whatever was ticked, and a pick that counted rows the
 * library has since lost would hold the cap with phantoms nobody can untick.
 */

import type { ScanNote } from '../core/store'
import type { Temper } from '../core/types'

/**
 * `null` is the within scope; a set is "across, and exactly these". One spec,
 * because the go button and "Scan them" both spend from it, and two buttons
 * that could disagree about what a press reads would make the cheaper-looking
 * one the lie.
 */
export type ScanPick = ReadonlySet<string> | null

/** The spec a press hands `scanForBridges`. An empty pick still carries
 *  `among`: the engine's loud refusal is reachable only if the emptiness
 *  arrives, and `{scope: 'across'}` alone would silently widen to recency. */
export function scanOpts(picks: ScanPick): { scope?: 'across'; among?: string[] } {
  return picks === null ? {} : { scope: 'across', among: [...picks] }
}

/**
 * How many of the ticked ids still exist in the library.
 *
 * A picked exploration another tab has since forgotten renders no row, so it
 * must hold no seat: counted, it would keep the cap
 * closed with phantoms nobody can untick, and an all-phantom pick would wear
 * six ticks over a pool that reads nothing.
 */
export function tickedCount(picks: ReadonlySet<string>, liveIds: ReadonlySet<string>): number {
  let n = 0
  for (const id of picks) if (liveIds.has(id)) n++
  return n
}

/**
 * Whether the note describes the reading a press would repeat — temper, model
 * and pool all unchanged. The pool clause is what keeps an across scan from
 * wearing a within scan's "nothing would change", and the other way around.
 */
export function isSameReading(
  note: ScanNote | undefined,
  cur: { temper: Temper; modelId: string; pool: string },
): boolean {
  return (
    note !== undefined &&
    note.temper === cur.temper &&
    note.modelId === cur.modelId &&
    note.pool === cur.pool
  )
}
